import styled from '@emotion/styled';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import DownloadIcon from '~/icons/DownloadIcon';
import { fmPrice } from '~/utils/amount';
import { formatDate } from '~/utils/datetime';
import { openInNewTab } from '~/utils/utils';
import { useEstimateListContext } from '../hooks/useEstimateListContext';
import EstimateDueStatus from '../EstimateDueStatus';
import EstimateStatusPills from './EstimateStatusPills';
import EstimateContextMenu from './EstimateContextMenu';

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: var(--neut6);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--neut5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface ICellContentProps {
  estimateReferenceId: string;
  identifier: string;
}

const defaultValueDash = '--';

const CellContent = ({ estimateReferenceId, identifier }: ICellContentProps) => {
  const { listData, onOpenViewEstimateModal } = useEstimateListContext();
  const thisEstimateData = listData[estimateReferenceId];

  if (typeof thisEstimateData === 'undefined') {
    return null;
  }

  if (identifier === 'estimate_number') {
    return (
      <div
        className="cursor-pointer"
        onClick={() => {
          onOpenViewEstimateModal(thisEstimateData.estimate_uuid);
        }}
      >
        <Text className="bold">{thisEstimateData.estimate_number}</Text>
        <Gap t={2} />
        <Text size="xs" variant="secondary" className="bold">
          {thisEstimateData.buyer_company_name}
        </Text>
      </div>
    );
  }

  if (identifier === 'created_on' || identifier === 'due_date') {
    const formattedDate = thisEstimateData[identifier]
      ? formatDate(thisEstimateData[identifier])
      : null;

    return (
      <div>
        <Text>{formattedDate}</Text>
        {identifier === 'due_date' && <EstimateDueStatus estimateData={thisEstimateData} />}
      </div>
    );
  }

  if (identifier === 'amount') {
    return <Text>{fmPrice(thisEstimateData.currency, thisEstimateData.total_amount)}</Text>;
  }

  if (identifier === 'status') {
    return <EstimateStatusPills estimateData={thisEstimateData} />;
  }

  if (identifier === 'download') {
    const handleDownloadClick = () => {
      if (thisEstimateData.pdf_url) {
        openInNewTab(thisEstimateData.pdf_url);
      }
    };

    return (
      <ActionsWrapper>
        <DownloadButton
          onClick={handleDownloadClick}
          disabled={!thisEstimateData.pdf_url}
          title="Download PDF"
        >
          <DownloadIcon width={16} height={16} />
        </DownloadButton>
      </ActionsWrapper>
    );
  }

  if (identifier === 'actions') {
    return (
      <div className="center">
        <EstimateContextMenu estimateReferenceId={estimateReferenceId} />
      </div>
    );
  }

  return <p data-text-type="main-text">{defaultValueDash}</p>;
};

export default CellContent;
